import { wccModules } from "enums/wccModules";
import { SmartDropdownOptions } from "extensions/knockout/bindings/smartDropdown";
import { inject, injectable } from "inversify";
import { Observable, PureComputed, Subscribable, SubscribableOrNullableValue } from "knockout";
import NodeAttributeTracker from "../../../managers/nodes/attributeTracker";
import { EffectsContainer } from "../../../mixins/withEffects";
import DropdownVM, { Dropdown, DropdownItem, DropdownItemOrPredicate, JSONDropdown } from "../base/base";
import './select.html';

export interface JSONSelectDropdown<T = any> extends JSONDropdown<T> {
    Disabled?: boolean

    placeholder?: SubscribableOrNullableValue<string>
    container?: SubscribableOrNullableValue<string | HTMLElement>
    customScroll?: SubscribableOrNullableValue<boolean>

    noDefault?: boolean    
    disabled?: boolean
    highlightActiveItem?: boolean
}

export class SelectDropdown<T = any> extends Dropdown<T> {
    selectedItem: PureComputed<DropdownItem<T> | undefined>
    disabled: Observable<boolean>        

    placeholder: Subscribable<string | undefined>
    container: Subscribable<string | HTMLElement | undefined>

    hasCustomScroll: Subscribable<boolean>

    highlightActiveItem: boolean
    noDefault: boolean

    constructor(data?: JSONSelectDropdown<T>) {
        super(data);

        const defaultValue = data?.noDefault === true ? undefined : this.items.first();

        this.selectedItem = ko.observable().default(defaultValue);
        this.disabled = ko.observable(data?.Disabled ?? data?.disabled ?? false);

        this.placeholder = ko.flattenComputed(data?.placeholder);
        this.container = ko.flattenComputed(data?.container);

        this.hasCustomScroll = ko.flattenComputed(data?.customScroll, false);

        this.noDefault = data?.noDefault ?? false;
        this.highlightActiveItem = data?.highlightActiveItem ?? false;        
    }

    select(itemOrPredicate: DropdownItemOrPredicate<T>) {
        let item = this.findItem(itemOrPredicate);

        if (item !== this.selectedItem())
            this.selectedItem(item);
    }

    clear() {
        this.selectedItem(undefined);
    }
}

@injectable()
export default class SelectDropdownVM extends DropdownVM {
    private dropdown: SelectDropdown

    dropdownNode = ko.observable<Node>();

    isOpen = ko.observable(false);

    headerNodes: JQuery<any>
    itemNodes: JQuery<any>

    highlightActiveItem: boolean

    items: Subscribable<Array<DropdownItem>>
    selectedItem: PureComputed<DropdownItem | undefined>

    placeholder: Subscribable<string | undefined>    
    
    isHeaderAllowed: Subscribable<boolean>
    hasMultipleItems: Subscribable<boolean>
    hasSingleItem: Subscribable<boolean>

    smartDropdownConfig: SmartDropdownOptions

    constructor(
        @inject(wccModules.componentConfig) model: SelectDropdown,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        super(model, info);

        this.dropdown = model;
        this.highlightActiveItem = model.highlightActiveItem;

        this.placeholder = model.placeholder;

        this.headerNodes = $(info.templateNodes).filter('select-head').contents();
        this.itemNodes = $(info.templateNodes).filter('select-item').contents();

        this.items = model.items;
        this.selectedItem = model.selectedItem;

        this.isHeaderAllowed = ko.pureComputed(() => this.dropdown.noDefault || this.selectedItem() != undefined);
        this.hasMultipleItems = ko.pureComputed(() => this.items().length > 1 || this.dropdown.noDefault);
        this.hasSingleItem = ko.pureComputed(() => this.items().length == 1 && !this.dropdown.noDefault);

        this.smartDropdownConfig = {
            container: model.container
        }

        effects.register((items, selectedItem) => {
            if (selectedItem != undefined && !items.includes(selectedItem))
                this.selectedItem(undefined);
        }, [this.items, this.selectedItem]);

        effects.register(new NodeAttributeTracker(this.dropdownNode, 'class', () => true, node => this.isOpen(node.hasClass('open'))));

        ko.applyBindingsToNode(info.element, {
            click: (m: any, e: JQueryEventObject) => this.onClick(e),

            css: {
                'wcc-dropdown--custom-scroll': model.hasCustomScroll
            }
        });

        if (!_(this.headerNodes).any())
            this.headerNodes = $(`
<!-- ko if: $data -->
<span class="wcc-link" data-bind="text: title"></span>
<!-- /ko -->

<!-- ko ifnot: $data -->
<span class="wcc-link" data-bind="text: $select.placeholder"></span>
<!-- /ko -->`);

        if (!_(this.itemNodes).any())
            this.itemNodes = $('<span data-bind="text: title"></span>');        
    }

    select = (itemOrPredicate: DropdownItemOrPredicate) => this.dropdown.select(itemOrPredicate)

    private onClick(e: JQueryEventObject) {
        if (this.dropdown.disabled()) {
            e.stopPropagation();
            e.preventDefault();
        }
    };
}